import React, { Component } from "react";
import PropTypes from "prop-types";

import { isFunction, gatherClasses } from "../../lib/utils";
import {
  getWidth,
  getHeight,
  getOffset,
  precisionMath,
  checkValue
} from "./sliderUtils";
import ProgressBar from "./ProgressBar";
import Handle from "./Handle";
import Graduated from "./Graduated";
import "./slider.scss";

class Slider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: this.checkValue(props.defaultValue, props)
    };
    this.barRef = React.createRef();
  }

  getSplitCount() {
    const { min, step } = this.props;
    const max = this.getMax();
    return precisionMath((max - min) / step);
  }

  getLabelSplitCount() {
    const { min, labelStep } = this.props;
    const max = this.getMax();
    return precisionMath((max - min) / labelStep);
  }

  getMax(props) {
    const { max, min, step } = props || this.props;
    return precisionMath(Math.floor((max - min) / step) * step + min);
  }

  getValue() {
    // eslint-disable-next-line react/prop-types
    const { value } = this.props;
    const { value: stateValue } = this.state;

    return typeof value === "undefined" ? stateValue : this.checkValue(value);
  }

  getHeight() {
    if (this.barRef.current) {
      return getHeight(this.barRef.current);
    }
    return 0;
  }

  getWidth() {
    if (this.barRef.current) {
      return getWidth(this.barRef.current);
    }
    return 0;
  }

  getValueByOffset(offset) {
    const { step } = this.props;
    const count = this.getSplitCount();

    let value = 0;

    // eslint-disable-next-line no-restricted-globals
    if (isNaN(offset)) {
      return value;
    }

    const barWidth = this.getWidth();
    value = Math.round(offset / (barWidth / count)) * step;

    return precisionMath(value);
  }

  getValueByPosition = event => {
    const { min } = this.props;
    const barOffset = getOffset(this.barRef.current);
    const offset = event.pageX - barOffset.left;
    const value = offset;

    return this.getValueByOffset(value) + min;
  };

  handleClick = event => {
    const { disabled, onChange } = this.props;

    if (disabled) {
      return;
    }
    const value = this.getValueByPosition(event);
    const nextValue = this.checkValue(value);

    this.setState({ value: nextValue });

    if (onChange) {
      onChange(nextValue, event);
    }
  };

  handleDragMove = event => {
    const { onMove } = this.props;
    const value = this.getValueByPosition(event);
    const nextValue = this.checkValue(value);

    this.setState({ value: nextValue });

    if (onMove) {
      onMove(nextValue, event);
    }
  };

  handleDragEnd = event => {
    const { onChange } = this.props;
    const { value: stateValue } = this.state;

    if (onChange) {
      onChange(stateValue, event);
    }
  };

  checkValue(value, props) {
    const { min } = props || this.props;
    const max = this.getMax(props);
    return checkValue(value, min, max);
  }

  renderGraduated() {
    const { labelStep, step, min, renderLabel } = this.props;
    const max = this.getMax();
    const count = labelStep ? this.getLabelSplitCount() : this.getSplitCount();
    const value = this.getValue();

    return (
      <Graduated
        step={labelStep || step}
        min={min}
        max={max}
        count={count}
        value={value}
        renderLabel={renderLabel}
      />
    );
  }

  renderHandle() {
    const { min, tooltip, disabled } = this.props;
    const max = this.getMax();
    const value = this.getValue();

    return (
      <Handle
        position={((value - min) / (max - min)) * 100}
        disabled={disabled}
        tooltip={tooltip}
        value={value}
        onDragMove={this.handleDragMove}
        onDragEnd={this.handleDragEnd}
      />
    );
  }

  renderProgress() {
    const { min } = this.props;
    const max = this.getMax();
    const value = this.getValue();
    return <ProgressBar start={0} end={((value - min) / (max - min)) * 100} />;
  }

  renderSelectedValue() {
    const { selectedValueRenderer, vertical } = this.props;
    const { value: stateValue } = this.state;

    return isFunction(selectedValueRenderer)
      ? selectedValueRenderer(stateValue, vertical)
      : stateValue;
  }

  render() {
    const { graduated, disabled, vertical, tooltip } = this.props;

    return (
      <div
        className={gatherClasses(
          this.props,
          "oui-slider-container",
          vertical ? "oui-vertical" : "oui-horizontal"
        )}
      >
        {!tooltip && vertical && (
          <div className="oui-value-container">
            <div>{this.renderSelectedValue()}</div>
          </div>
        )}
        <div
          className={gatherClasses(
            null,
            "oui-slider",
            disabled ? "oui-disabled" : "",
            graduated ? "oui-graduated" : ""
          )}
          role="presentation"
        >
          <div
            className="oui-slider-bar"
            ref={this.barRef}
            onClick={this.handleClick}
            aria-hidden="true"
          >
            {this.renderProgress()}
            {graduated && this.renderGraduated()}
          </div>
          {this.renderHandle()}
        </div>
        {!tooltip && !vertical && (
          <div className="oui-value-container">
            <div>{this.renderSelectedValue()}</div>
          </div>
        )}
      </div>
    );
  }
}

Slider.propTypes = {
  defaultValue: PropTypes.number,
  disabled: PropTypes.bool,
  graduated: PropTypes.bool,
  labelStep: PropTypes.number,
  // eslint-disable-next-line react/no-unused-prop-types
  max: PropTypes.number.isRequired,
  min: PropTypes.number,
  onChange: PropTypes.func,
  onMove: PropTypes.func,
  renderLabel: PropTypes.func,
  selectedValueRenderer: PropTypes.func,
  step: PropTypes.number,
  tooltip: PropTypes.bool,
  vertical: PropTypes.bool
};

Slider.defaultProps = {
  defaultValue: 0,
  disabled: false,
  graduated: true,
  labelStep: 1,
  min: 0,
  onChange: null,
  onMove: null,
  renderLabel: label => label,
  selectedValueRenderer: null,
  step: 1,
  tooltip: true,
  vertical: false
};

Slider.displayName = "OrionSlider";

export default Slider;
